/**
 * The address bar as the source of truth for where you are.
 *
 * There was no routing at all: the section lived in React state, so reloading on the
 * paywall put you back on the daybook, the browser's back button left the app entirely,
 * and no view could be linked to. The backend has always served the SPA shell for every
 * path, so this is purely the missing client half.
 *
 * Hand-rolled rather than a router dependency. There are nine sections and no nested
 * routes; what a router would add here is a rendering model, and this app's rendering
 * model is already `view.name === …` in one component.
 *
 * The path carries the section and the query carries the view's context, which is the
 * split that keeps links honest: `/funnel?name=purchase&day=2026-09-05` is a page, while
 * the day it happens to be showing is a parameter of it.
 */

/** Sections, in the order they appear in the nav. `daybook` is the empty path. */
export const SECTIONS = [
  'daybook',
  'metrics',
  'funnel',
  'events',
  'pulse',
  'paywall',
  'briefing',
  'settings',
  'admin',
] as const

export type Section = (typeof SECTIONS)[number]

/** The URL segment for each section, and the section for each segment. */
const SEGMENTS: Record<Section, string> = {
  daybook: '',
  metrics: 'metrics',
  funnel: 'funnel',
  events: 'events',
  pulse: 'pulse',
  paywall: 'paywall',
  briefing: 'briefing',
  settings: 'settings',
  admin: 'admin',
}

export interface Location {
  section: Section
  /** The named funnel, when the section is `funnel`. */
  funnel?: string
  /** A report date the view was opened on — a day from the daybook, or a window end. */
  day?: string
  /** Which property, so a reload does not silently answer about a different client. */
  property?: string
}

/** A YYYY-MM-DD day, checked because it lands in a query the API will be asked. */
function cleanDay(value: string | null): string | undefined {
  return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined
}

/**
 * Read the current address.
 *
 * An unknown path falls back to the daybook rather than rendering nothing: the shell is
 * served for every path, so a typo or a stale bookmark reaches this code rather than a
 * 404, and a blank page would read as the app being broken.
 */
export function readLocation(url: string = window.location.href): Location {
  const parsed = new URL(url, window.location.origin)
  const segment = parsed.pathname.replace(/^\/+|\/+$/g, '').split('/')[0] ?? ''
  const section =
    (SECTIONS.find((name) => SEGMENTS[name] === segment) as Section | undefined) ?? 'daybook'
  const funnel = parsed.searchParams.get('name')?.trim()
  const property = parsed.searchParams.get('property')?.trim()
  return {
    section,
    funnel: funnel || undefined,
    day: cleanDay(parsed.searchParams.get('day')),
    property: property && /^\d{1,20}$/.test(property) ? property : undefined,
  }
}

/** The address for a location, with empty parameters left off. */
export function pathFor(location: Location): string {
  const query = new URLSearchParams()
  if (location.section === 'funnel' && location.funnel) query.set('name', location.funnel)
  if (location.day) query.set('day', location.day)
  if (location.property) query.set('property', location.property)
  const suffix = query.toString()
  return `/${SEGMENTS[location.section]}${suffix ? `?${suffix}` : ''}`
}

/**
 * Put a location in the address bar, adding a history entry only when it is a new one.
 *
 * `replaceState` for a location that only refines the current one — picking a different
 * day inside the same section — so the back button leaves the section rather than
 * walking back through every date the reader tried.
 */
export function writeLocation(location: Location): void {
  const next = pathFor(location)
  if (next === window.location.pathname + window.location.search) return
  const current = readLocation()
  if (current.section === location.section) window.history.replaceState(null, '', next)
  else window.history.pushState(null, '', next)
}
